import React from 'react';
import { Hash } from 'lucide-react';
import {
  getTagEnhancedDescription,
  generateDefaultTagDescription,
  generateTagSEOH1,
} from '../data/tagDescriptions';

interface TagIntroProps {
  tag: string;
  tagSlug: string;
  locale: string;
  postCount: number;
}

/**
 * TagIntro Component
 *
 * Renders enhanced SEO introduction for tag pages
 * Includes localized 150-250 word description
 * Auto-generates description for tags without custom content
 * SSR-friendly, no client-side dependencies
 */
export const TagIntro: React.FC<TagIntroProps> = ({ tag, tagSlug, locale, postCount }) => {
  const enhanced = getTagEnhancedDescription(tag, locale, postCount);

  let description: string;
  let h1: string;

  if (enhanced) {
    // Use custom enhanced description
    const localeKey = locale as keyof typeof enhanced.seoDescription;
    description = enhanced.seoDescription[localeKey] || enhanced.seoDescription.en;
    h1 = enhanced.seoH1[localeKey] || enhanced.seoH1.en;
  } else {
    // Generate default description
    description = generateDefaultTagDescription(tag, locale, postCount);
    h1 = generateTagSEOH1(tag, locale);
  }

  return (
    <div className="mb-12">
      {/* Enhanced H1 */}
      <h1 className="text-3xl md:text-4xl font-bold text-charcoal-900 dark:text-white mb-6">
        {h1}
      </h1>

      {/* SEO-optimized description */}
      <div className="bg-gradient-to-br from-accent-50 to-accent-100/50 dark:from-accent-900/20 dark:to-accent-800/10 rounded-xl p-6 md:p-8 border border-accent-200 dark:border-accent-700/30 mb-8">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 mt-1">
            <div className="w-12 h-12 rounded-lg bg-accent-600 dark:bg-accent-700 flex items-center justify-center">
              <Hash className="w-6 h-6 text-white" />
            </div>
          </div>
          <div className="flex-1">
            <p className="text-base leading-relaxed text-neutral-700 dark:text-neutral-300">
              {description}
            </p>
          </div>
        </div>
      </div>

      {/* Tag Statistics */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <div className="bg-white dark:bg-charcoal-800 rounded-lg p-4 border border-neutral-200 dark:border-charcoal-700">
          <div className="text-sm text-neutral-600 dark:text-neutral-400 mb-1">
            {locale === 'de' && 'Artikel'}
            {locale === 'pt-BR' && 'Artigos'}
            {locale === 'ms-MY' && 'Artikel'}
            {locale === 'es' && 'Artículos'}
            {locale === 'fr' && 'Articles'}
            {locale === 'ja' && '記事'}
            {locale === 'id' && 'Artikel'}
            {(locale === 'en' || !['de', 'pt-BR', 'ms-MY', 'es', 'fr', 'ja', 'id'].includes(locale)) && 'Articles'}
          </div>
          <div className="text-2xl font-bold text-accent-600 dark:text-accent-400">
            {postCount}
          </div>
        </div>

        <div className="bg-white dark:bg-charcoal-800 rounded-lg p-4 border border-neutral-200 dark:border-charcoal-700">
          <div className="text-sm text-neutral-600 dark:text-neutral-400 mb-1">
            {locale === 'de' && 'Kategorie'}
            {locale === 'pt-BR' && 'Categoria'}
            {locale === 'ms-MY' && 'Kategori'}
            {locale === 'es' && 'Categoría'}
            {locale === 'fr' && 'Catégorie'}
            {locale === 'ja' && 'カテゴリ'}
            {locale === 'id' && 'Kategori'}
            {(locale === 'en' || !['de', 'pt-BR', 'ms-MY', 'es', 'fr', 'ja', 'id'].includes(locale)) && 'Category'}
          </div>
          <div className="text-sm font-semibold text-charcoal-900 dark:text-white">
            {locale === 'de' && 'KI-Wasserzeichen'}
            {locale === 'pt-BR' && 'Marcas d\'água IA'}
            {locale === 'ms-MY' && 'Watermark AI'}
            {locale === 'es' && 'Marcas de agua IA'}
            {locale === 'fr' && 'Filigranes IA'}
            {locale === 'ja' && 'AI透かし'}
            {locale === 'id' && 'Watermark AI'}
            {(locale === 'en' || !['de', 'pt-BR', 'ms-MY', 'es', 'fr', 'ja', 'id'].includes(locale)) && 'AI Watermarks'}
          </div>
        </div>

        <div className="bg-white dark:bg-charcoal-800 rounded-lg p-4 border border-neutral-200 dark:border-charcoal-700">
          <div className="text-sm text-neutral-600 dark:text-neutral-400 mb-1">
            {locale === 'de' && 'Aktualisiert'}
            {locale === 'pt-BR' && 'Atualizado'}
            {locale === 'ms-MY' && 'Dikemaskini'}
            {locale === 'es' && 'Actualizado'}
            {locale === 'fr' && 'Mis à jour'}
            {locale === 'ja' && '更新日'}
            {locale === 'id' && 'Diperbarui'}
            {(locale === 'en' || !['de', 'pt-BR', 'ms-MY', 'es', 'fr', 'ja', 'id'].includes(locale)) && 'Updated'}
          </div>
          <div className="text-sm font-semibold text-charcoal-900 dark:text-white">
            2025
          </div>
        </div>
      </div>
    </div>
  );
};

export default TagIntro;
